import ai.rules.BucketFlow
import ai.rules.SankeyBand
import ai.rules.SankeyGeometry
import ai.rules.SankeyNode
import ai.rules.SankeyRibbon
import ai.rules.SankeyScene
import ai.rules.SankeyText
import ai.rules.SankeyTextAnchor

/**
 * SVG renderer for the Needs & Wants Flow widget. Returns an `<svg>…</svg>`
 * string ready for innerHTML.
 *
 * All the geometry — conservation-respecting band layout, the fanning ribbons,
 * node stacking, drill-level selection, label placement and the gutter — lives
 * in the shared [SankeyGeometry], so iOS (SwiftUI Path) and Android (Compose
 * Canvas) draw the identical diagram from the identical numbers. This file only
 * turns a [SankeyScene] into markup.
 *
 * Every band carries `data-*` attributes — category, dollar amount, and share of
 * its container — so a strand too thin for an inline label still explains itself
 * on hover. The tooltip itself is a positioned div wired in
 * WebDashboardRedesigned so it appears instantly, instead of the browser's slow
 * native `<title>`.
 */
object SankeyChart {

    fun renderToSvg(flow: BucketFlow, widthPx: Int, heightPx: Int, isDark: Boolean): String {
        val scene = SankeyGeometry.layout(flow, widthPx.toDouble(), heightPx.toDouble(), isDark)

        val sb = StringBuilder()
        sb.append("""<svg viewBox="0 0 $widthPx $heightPx" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">""")

        scene.emptyText?.let {
            sb.append(text(it))
            sb.append("</svg>")
            return sb.toString()
        }

        sb.append(
            """<style>.sk-ribbon{opacity:${trimZero(SankeyGeometry.RIBBON_OPACITY)};transition:opacity .12s}""" +
                """.sk-band:hover .sk-ribbon{opacity:${trimZero(SankeyGeometry.RIBBON_HOVER_OPACITY)}}.sk-band{cursor:default}</style>""",
        )
        for (b in scene.bands) sb.append(band(b))
        sb.append("</svg>")
        return sb.toString()
    }

    /** A band: its ribbon, its node, then its label — painted in that order so a
     *  later ribbon washes over an earlier label, as it always has. */
    private fun band(b: SankeyBand): String {
        val sb = StringBuilder()
        if (b.interactiveId != null) sb.append("""<g id="${b.interactiveId}" style="cursor:pointer;">""")
        sb.append(bandGroup(b))
        b.ribbon?.let { sb.append(ribbon(it)) }
        sb.append(node(b.node))
        sb.append("</g>")
        if (b.interactiveId != null) sb.append("</g>")
        b.text?.let { sb.append(text(it)) }
        return sb.toString()
    }

    /** Opens the hoverable group carrying the data the custom tooltip reads. */
    private fun bandGroup(b: SankeyBand): String {
        val base = """<g class="sk-band" data-cat="${attr(b.label)}" data-amt="${attr(b.amountText)}" data-color="${b.colorHex}""""
        return if (b.percentOfParent != null && b.parentLabel != null) {
            """$base" data-pct="${b.percentOfParent}" data-parent="${attr(b.parentLabel!!)}">"""
        } else {
            "$base\">"
        }
    }

    /** Flush stacking at the source is what makes the children sum to the parent,
     *  so ribbons are drawn at full thickness — the hover-brighten and the gapped
     *  destination nodes provide the visual separation, not an inset. */
    private fun ribbon(r: SankeyRibbon): String {
        val top = r.topEdge
        val bottom = r.bottomEdge
        val d = "M ${top.start.x} ${top.start.y} " +
            "C ${top.control1.x} ${top.control1.y}, ${top.control2.x} ${top.control2.y}, ${top.end.x} ${top.end.y} " +
            "L ${bottom.start.x} ${bottom.start.y} " +
            "C ${bottom.control1.x} ${bottom.control1.y}, ${bottom.control2.x} ${bottom.control2.y}, ${bottom.end.x} ${bottom.end.y} Z"
        return """<path class="sk-ribbon" d="$d" fill="${r.colorHex}"/>"""
    }

    private fun node(n: SankeyNode): String {
        val opacity = if (n.opacity >= 1.0) "" else """ opacity="${n.opacity}""""
        return """<rect class="sk-node" x="${n.x}" y="${n.y}" width="${n.width}" height="${n.height}" rx="${n.cornerRadius}" fill="${n.colorHex}"$opacity/>"""
    }

    private fun text(t: SankeyText): String {
        val anchor = if (t.anchor == SankeyTextAnchor.MIDDLE) """ text-anchor="middle"""" else ""
        return """<text x="${t.x}" y="${t.baselineY}" fill="${t.colorHex}" font-size="${t.fontSize}"$anchor>${t.text}</text>"""
    }

    /** CSS wants `.32`, not `0.32`. */
    private fun trimZero(v: Double): String = v.toString().removePrefix("0")

    private fun attr(s: String): String =
        HtmlEscape.escape(s)
}
